import {
    afterNextRender,
    ChangeDetectionStrategy,
    computed,
    Component,
    type ElementRef,
    inject,
    Injector,
    input,
    linkedSignal,
    output,
    viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { RouterOutlet } from '@angular/router';
import { injectUser } from '@quipex/core/auth';
import { injectViewportBreakpoints } from '@quipex/core/utils';
import type { NavButton, NavItem } from '@quipex/shared/ui';
import {
    AppTopBar,
    type AppTopBarShortcutsByScope,
} from '../app-top-bar/app-top-bar';
import {
    injectAppTopBarEligibility,
    type AppTopBarShortcut,
} from '../app-top-bar/app-top-bar-runtime';
import { Header } from '../header/header';
import { Sidebar } from '../sidebar/sidebar';
import { Splash } from '../splash';
import { UtilityDock } from '../utility-dock/utility-dock';

type ShellMode = 'mobile' | 'compact' | 'persistent';

@Component({
    selector: 'qpx-layout',
    imports: [
        MatButtonModule,
        RouterOutlet,
        AppTopBar,
        Sidebar,
        Header,
        Splash,
        UtilityDock,
    ],
    templateUrl: './layout.html',
    styleUrls: ['./layout.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'contents',
    },
})
export class Layout {
    // #region Dependency injection
    private readonly viewport = injectViewportBreakpoints();
    private readonly injector = inject(Injector);
    private readonly me = injectUser();
    protected readonly topBarEligible = injectAppTopBarEligibility();
    // #endregion

    // #region Outputs
    public readonly findBuildingRequested = output<HTMLElement | undefined>();
    public readonly whatsNewRequested = output<void>();
    // #endregion

    // #region Inputs
    public readonly hasUnreadReleaseNotes = input(false);
    public readonly extraGeneralNavItems = input<readonly NavItem[]>([]);
    public readonly manageCompanyShortcut = input<AppTopBarShortcut | null>(
        null
    );
    public readonly topBarShortcutsByScope = input<AppTopBarShortcutsByScope>(
        {}
    );
    public readonly utilityActions = input<readonly NavButton[]>([]);
    // #endregion

    // #region State
    protected readonly isRailOrWider = this.viewport.atLeast('tablet768');
    private readonly compactViewport = this.viewport.lessThan('laptop');
    protected readonly isCompactRail = computed(
        () => this.isRailOrWider() && this.compactViewport()
    );
    private readonly shellMode = computed<ShellMode>(() => {
        if (!this.isRailOrWider()) return 'mobile';
        return this.isCompactRail() ? 'compact' : 'persistent';
    });
    protected readonly menuExpanded = linkedSignal<ShellMode, boolean>({
        source: this.shellMode,
        computation: (mode) => {
            if (mode === 'mobile') return false;
            return mode === 'persistent';
        },
    });
    protected readonly sidebarHovered = linkedSignal({
        source: () => this.isCompactRail(),
        computation: () => false,
    });
    protected readonly effectiveSidebarExpanded = computed(() =>
        this.isRailOrWider()
            ? this.menuExpanded() || this.sidebarHovered()
            : this.menuExpanded()
    );
    // #endregion

    // #region Query state
    protected readonly isIdle = this.me.isIdle;
    protected readonly isLoading = this.me.isLoading;
    protected readonly isError = this.me.isError;
    // #endregion

    // #region Template references
    private readonly header = viewChild<Header>('header');
    private readonly routedContent =
        viewChild<ElementRef<HTMLElement>>('routedContent');
    private readonly sidebar = viewChild<Sidebar>('sidebar');
    private readonly utilityDock = viewChild<UtilityDock>('utilityDock');
    // #endregion

    // #region Public methods
    public focusRoutedContent(): void {
        this.routedContent()?.nativeElement.focus();
    }

    public focusUtilityAction(key: string): void {
        this.utilityDock()?.focusAction(key);
    }
    // #endregion

    // #region Template methods
    protected onToggleChange(expanded: boolean): void {
        const wasExpanded = this.menuExpanded();
        this.menuExpanded.set(expanded);
        if (!expanded) {
            this.sidebarHovered.set(false);
        }

        if (!this.isRailOrWider() && expanded && !wasExpanded) {
            afterNextRender(() => this.sidebar()?.focusFirstControl(), {
                injector: this.injector,
            });
        }
    }

    protected onHeaderToggle(): void {
        this.onToggleChange(!this.menuExpanded());
    }

    protected onSidebarHoveredChange(hovered: boolean): void {
        this.sidebarHovered.set(hovered);
    }

    protected onHeaderMouseLeave(event: MouseEvent): void {
        if (
            !this.isCompactRail() ||
            isWithin(event.relatedTarget, 'qpx-sidebar')
        ) {
            return;
        }

        this.sidebarHovered.set(false);
    }

    protected restoreHeaderToggleFocus(): void {
        if (this.isRailOrWider()) return;

        afterNextRender(() => this.header()?.focusNavigationToggle(), {
            injector: this.injector,
        });
    }

    protected openFindBuilding(origin?: HTMLElement): void {
        if (!origin && !this.isRailOrWider()) {
            this.header()?.focusNavigationToggle();
        }

        this.findBuildingRequested.emit(origin);
    }

    protected openWhatsNew(): void {
        this.whatsNewRequested.emit();
    }
    // #endregion
}

function isWithin(target: EventTarget | null, selector: string): boolean {
    return target instanceof Element && target.closest(selector) !== null;
}
